import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment, useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useFormContext } from 'react-hook-form';
import { UI_NAMESPACE } from '../../../i18n';
import { cn } from '../../../lib/utils';
import { FormField } from '../../ui/form';
const CENTS_PER_EURO = 100;
const MAX_FRACTION_DIGITS = 2;
const ITALIAN_LOCALE_TAG = 'it-IT';
const GROUPED_ITALIAN = /^\d{1,3}(\.\d{3})+(,\d{1,2})?$/;
const PLAIN_ITALIAN = /^\d+(,\d{1,2})?$/;
const DOT_DECIMAL = /^\d+\.\d{1,2}$/;
const roundToCents = (amount) => Math.round(amount * CENTS_PER_EURO) / CENTS_PER_EURO;
/**
 * Testo digitato → importo assoluto in euro, con le convenzioni italiane
 * ("1.234,56", "1234,5", "€ 12"). `undefined` = campo vuoto, `null` = testo
 * non interpretabile. Il segno si ignora (il filtro è sul valore assoluto);
 * un solo punto seguito da 1–2 cifre ("12.50") vale come decimale, altrimenti
 * i punti sono separatori delle migliaia. Oltre i centesimi non è un importo.
 */
export function parseItalianAmount(text) {
    const compact = text.replace(/[\s€]/g, '').replace(/^[+-]/, '');
    if (compact === '')
        return undefined;
    let normalized = null;
    if (GROUPED_ITALIAN.test(compact)) {
        normalized = compact.replace(/\./g, '').replace(',', '.');
    }
    else if (PLAIN_ITALIAN.test(compact)) {
        normalized = compact.replace(',', '.');
    }
    else if (DOT_DECIMAL.test(compact)) {
        normalized = compact;
    }
    if (normalized === null)
        return null;
    const amount = Number(normalized);
    return Number.isFinite(amount) ? roundToCents(amount) : null;
}
/** Importo → testo del campo ("1.234,56", "1.500"): rilegge con `parseItalianAmount`. */
export function formatItalianAmount(amount) {
    if (amount === undefined)
        return '';
    return new Intl.NumberFormat(ITALIAN_LOCALE_TAG, {
        minimumFractionDigits: Number.isInteger(amount) ? 0 : MAX_FRACTION_DIGITS,
        maximumFractionDigits: MAX_FRACTION_DIGITS,
    }).format(amount);
}
/** Intervallo incoerente: il minimo supera il massimo. */
export function isAmountRangeInvalid(value) {
    return (value?.min !== undefined &&
        value?.max !== undefined &&
        value.min > value.max);
}
/** `{}` è l'intervallo vuoto: nessun estremo. */
export function isAmountRangeEmpty(value) {
    return value?.min === undefined && value?.max === undefined;
}
/**
 * Stato di un estremo: il testo resta quello digitato; si riscrive solo
 * quando il valore cambia da fuori (bozza azzerata, reset all'apertura), non
 * all'eco di ciò che l'utente stesso ha appena emesso.
 */
function useAmountBound(value) {
    const [state, setState] = useState({
        text: formatItalianAmount(value),
        isTextInvalid: false,
    });
    const lastEmittedRef = useRef(value);
    useEffect(() => {
        if (value === lastEmittedRef.current)
            return;
        lastEmittedRef.current = value;
        setState({ text: formatItalianAmount(value), isTextInvalid: false });
    }, [value]);
    const onTextChange = (text) => {
        const parsed = parseItalianAmount(text);
        const emitted = parsed === null ? undefined : parsed;
        lastEmittedRef.current = emitted;
        setState({ text, isTextInvalid: parsed === null });
        return emitted;
    };
    return { ...state, onTextChange };
}
/**
 * Importo "da … €" / "a ∞ €" del pannello filtri. Parsing italiano, valore
 * assoluto; un testo non valido lascia vuoto quell'estremo e segna il campo;
 * min > max segna entrambi (`isAmountRangeInvalid`).
 */
export function AmountRangeInput({ value, onValueChange, boundLabels, disabled = false, testId, className, }) {
    const { t } = useTranslation(UI_NAMESPACE);
    const errorId = useId();
    const min = useAmountBound(value?.min);
    const max = useAmountBound(value?.max);
    const isRangeInvalid = isAmountRangeInvalid(value);
    const emit = (bound, amount) => {
        const next = {
            min: bound === 'min' ? amount : value?.min,
            max: bound === 'max' ? amount : value?.max,
        };
        onValueChange(Object.fromEntries(Object.entries(next).filter(([, v]) => v !== undefined)));
    };
    const errorKey = isRangeInvalid
        ? 'filterPanel.amount.invalidRange'
        : min.isTextInvalid || max.isTextInvalid
            ? 'filterPanel.amount.invalidAmount'
            : null;
    const renderBound = (bound) => {
        const state = bound === 'min' ? min : max;
        const isInvalid = state.isTextInvalid || isRangeInvalid;
        return (_jsxs("label", { className: cn('flex h-9 min-w-0 items-center gap-1.5 rounded-[10px] border border-input bg-background px-3 text-[13px] transition-colors', 'focus-within:border-brand focus-within:ring-[3px] focus-within:ring-brand-soft', isInvalid && 'border-destructive focus-within:border-destructive', disabled && 'cursor-not-allowed opacity-50'), children: [!boundLabels && (_jsx("span", { className: "shrink-0 text-n-500", children: t(bound === 'min'
                        ? 'filterPanel.amount.from'
                        : 'filterPanel.amount.to') })), _jsx("input", { type: "text", inputMode: "decimal", autoComplete: "off", disabled: disabled, value: state.text, "aria-label": boundLabels?.[bound] ??
                        t(bound === 'min'
                            ? 'filterPanel.amount.minLabel'
                            : 'filterPanel.amount.maxLabel'), "aria-invalid": isInvalid || undefined, "aria-describedby": errorKey ? errorId : undefined, placeholder: t(bound === 'min'
                        ? 'filterPanel.amount.minPlaceholder'
                        : 'filterPanel.amount.maxPlaceholder'), "data-testid": testId ? `${testId}-${bound}` : undefined, onChange: (event) => emit(bound, state.onTextChange(event.target.value)), className: "min-w-0 flex-1 bg-transparent text-right font-mono text-foreground outline-none placeholder:text-n-400 disabled:cursor-not-allowed" }), _jsx("span", { className: "shrink-0 text-n-500", children: t('filterPanel.amount.currency') })] }));
    };
    return (_jsxs("div", { "data-testid": testId, className: cn('flex flex-col gap-1', className), children: [_jsx("div", { className: cn('grid grid-cols-2', boundLabels ? 'gap-3' : 'gap-2'), children: ['min', 'max'].map((bound) => boundLabels ? (_jsxs("div", { className: "flex min-w-0 flex-col gap-1.5", children: [_jsx("span", { className: "text-xs font-medium text-n-700", children: boundLabels[bound] }), renderBound(bound)] }, bound)) : (_jsx(Fragment, { children: renderBound(bound) }, bound))) }), errorKey && (_jsx("p", { id: errorId, role: "alert", className: "text-xs text-destructive", children: t(errorKey) }))] }));
}
/** Adattatore react-hook-form: l'intervallo senza estremi si scrive `null`. */
export function AmountRangeForm({ name, ...props }) {
    const { control } = useFormContext();
    return (_jsx(FormField, { control: control, name: name, render: ({ field }) => (_jsx(AmountRangeInput, { ...props, value: field.value, onValueChange: (next) => field.onChange(isAmountRangeEmpty(next) ? null : next) })) }));
}
//# sourceMappingURL=amount-range-input.js.map